// useSubscriptionTier — reads the current user's tier from the profiles
// table via React Query. Drives the route guard, the sidebar filter, and
// the upgrade page copy.
//
// Non-logged-in users get `null`; they're handled by RequireAuth before
// this hook ever runs in the authenticated part of the app.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import type { SubscriptionTier } from "@/data/tierAccess";

export function useSubscriptionTier() {
  const { session, loading: authLoading } = useUserProfile();
  const userId = session?.user.id ?? null;

  const query = useQuery<SubscriptionTier | null>({
    queryKey: ["subscription-tier", userId],
    queryFn: async () => {
      if (!userId) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("subscription_tier")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) {
        console.error("[useSubscriptionTier] error:", error);
        throw error;
      }
      // ⚠️ DEFAULT DENY. This was "select" — a signed-in user with no
      // `profiles` row was handed the entry PAID tier.
      //
      // That was survivable only because every signup got a profiles row
      // unconditionally, so the branch never ran (0 of 15 users lacked one).
      // It stops being survivable with raisedbeef.com accounts: those users
      // deliberately have NO profiles row, so the old default would have given
      // every free signup paid Terminal access.
      //
      // It is also the masking that neutered BarnOnlyLock — which required
      // `tier === "free"` and therefore never fired — and the reason CLAUDE.md
      // says to gate on role, never on tier.
      const tier = (data?.subscription_tier ?? "free") as SubscriptionTier;
      return tier;
    },
    enabled: !authLoading && !!userId,
    staleTime: 5 * 60 * 1000,   // tiers rarely change mid-session
    gcTime:    10 * 60 * 1000,
  });

  return {
    tier: query.data ?? null,
    loading: authLoading || query.isLoading,
    error: query.error,
  };
}
